import { triggerBlobDownload } from './download';
import type {
  HeroPayload, LivePayload, UsagePayload, RoiPayload, RoiAssumptions, AssumptionsBody, BuilderPayload,
  AiPayload, CostOfBuildBody,
} from './statsTypes';

// SPEC-563 fix loop 2: the fetch core lives in ./http (no imports, so Node tests can load it).
import { ApiError, requestRaw } from './http';
export { ApiError };

// SPEC-434: exported so the promo read-layer fetchers (lib/promoApi.ts) reuse the
// exact auth-header + silent-refresh-on-401 core, rather than re-implementing fetch.
export async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await requestRaw(path, options);
  return res.json();
}

// SPEC-549: a file behind the auth guard, as a Blob (the Docs page plays its films from memory,
// so no film has an address that works without a login).
export async function requestBlob(path: string): Promise<Blob> {
  const res = await requestRaw(path);
  return res.blob();
}

// Dashboard
export const fetchDataStatus = () => request<any>('/api/data-status');
// SPEC-148: platform-health summary for the System lens — existing /health endpoint
// (returns { status: 'healthy'|'degraded'|'unhealthy', checks: {...} }); no new route.
export const fetchHealth = () => request<any>('/health');
// SPEC-148: admin intake queue for the System lens — existing /api/admin/intake
// (returns { items: [{ id, source, author, text, created_at, status }], count }).
// Admin/super_admin only; the System lens is the only caller. No new route.
export const fetchAdminIntake = () => request<{ items: any[]; count: number }>('/api/admin/intake');
export const fetchDashboardSummary = () => request<any>('/api/dashboard/summary');
export const fetchDashboardAlerts = () => request<any[]>('/api/dashboard/alerts');
export const fetchDashboardKPIs = () => request<any>('/api/dashboard/kpis');

// Products
export const fetchProducts = (params?: { search?: string; category?: string; limit?: number; offset?: number }) => {
  const q = new URLSearchParams();
  if (params?.search) q.set('search', params.search);
  if (params?.category) q.set('category', params.category);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  return request<any[]>(`/api/products?${q}`);
};
export const fetchProduct = (code: string) => request<any>(`/api/products/${code}`);

// Customers
export const fetchCustomers = (params?: { search?: string; status?: string; dormancy?: string; limit?: number; offset?: number }) => {
  const q = new URLSearchParams();
  if (params?.search) q.set('search', params.search);
  if (params?.status) q.set('status', params.status);
  if (params?.dormancy) q.set('dormancy', params.dormancy);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  return request<any[]>(`/api/customers?${q}`);
};
export const fetchCustomer = (code: string) => request<any>(`/api/customers/${code}`);

// Stock & Risk
export const fetchStockCoverage = () => request<any[]>('/api/stock/coverage');
export const fetchStockRisks = () => request<any[]>('/api/stock/risks');
export const fetchOverstock = () => request<any[]>('/api/stock/overstock');

// Movers & Dormant
export const fetchMovers = (moverClass?: string) => {
  const q = moverClass ? `?mover_class=${moverClass}` : '';
  return request<any[]>(`/api/movers${q}`);
};
export const fetchMoversSummary = () => request<any>('/api/movers/summary');
/** SPEC-495: /api/dormant answers an envelope, so a failed read is never an empty list.
 *  `rows` is null exactly when `state` is READ_FAILED; `cause` is what the server recorded. */
export interface DormantRead { rows: any[] | null; state: 'ok' | 'READ_FAILED'; cause?: string }
export const fetchDormant = async (window?: number): Promise<any[]> => {
  const q = window ? `?window=${window}` : '';
  const res = await request<DormantRead>(`/api/dormant${q}`);
  // The Sales tab's error branch already says "a failed read, not an empty list" — throw into it.
  if (res.state === 'READ_FAILED' || !Array.isArray(res.rows)) {
    // Plain words only: the Sales tab prints this message to exec readers.
    throw new Error('the list could not be read');
  }
  return res.rows;
};

// Velocity
export const fetchVelocity = (code: string) => request<any>(`/api/velocity/${code}`);

// Commodity
export const fetchCommodityPrices = () => request<any[]>('/api/commodity/prices');
export const fetchCommodityHistory = (code: string) => request<any[]>(`/api/commodity/prices/${code}`);
export const fetchCommodityFX = () => request<any>('/api/commodity/fx');
export const fetchCommodityFXHistory = (days?: number) => request<any[]>(`/api/commodity/fx/history?days=${days || 30}`);
export const fetchCommodityShipping = () => request<any>('/api/commodity/shipping');
export const fetchCommodityCPI = () => request<any>('/api/commodity/cpi');
export const fetchCommodityStatus = () => request<any[]>('/api/commodity/status');
export const refreshCommodity = () => request<any>('/api/commodity/refresh', { method: 'POST' });

// Sales
export const fetchSalesByRep = () => request<any[]>('/api/sales/by-rep');
export const fetchSalesByChannel = () => request<any[]>('/api/sales/by-channel');
// SPEC-565: Sales → Overview (figures on one served definition) and the QlikView check.
export const fetchSalesOverview = (period: string, cut: string) =>
  request<any>(`/api/sales/overview?period=${encodeURIComponent(period)}&cut=${encodeURIComponent(cut)}`);
export const fetchSalesReconciliation = () => request<any>('/api/sales/reconciliation');
export const postSalesReconciliation = (body: Record<string, string | null>) =>
  request<any>('/api/sales/reconciliation', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
export const fetchPromotionalAnalysis = (dates?: { from_date?: string | null; to_date?: string | null }) => {
  const q = new URLSearchParams();
  if (dates?.from_date) q.set('from_date', dates.from_date);
  if (dates?.to_date) q.set('to_date', dates.to_date);
  const qs = q.toString();
  return request<any>(`/api/sales/promotional-analysis${qs ? '?' + qs : ''}`);
};
export const fetchCategoryPerformance = (dates?: { from_date?: string | null; to_date?: string | null }) => {
  const q = new URLSearchParams();
  if (dates?.from_date) q.set('from_date', dates.from_date);
  if (dates?.to_date) q.set('to_date', dates.to_date);
  const qs = q.toString();
  return request<any>(`/api/sales/category-performance${qs ? '?' + qs : ''}`);
};
// SPEC-417 (Phase 1A): returns / credit-note analytics (fact_invoices.is_credit_note).
export const fetchReturnsAnalysis = (dates?: { from_date?: string | null; to_date?: string | null }) => {
  const q = new URLSearchParams();
  if (dates?.from_date) q.set('from_date', dates.from_date);
  if (dates?.to_date) q.set('to_date', dates.to_date);
  const qs = q.toString();
  return request<any>(`/api/fpa/returns-analysis${qs ? '?' + qs : ''}`);
};
export const fetchAtRiskCustomers = (sort?: string) => {
  const q = sort ? `?sort=${sort}` : '';
  return request<any[]>(`/api/customers/at-risk${q}`);
};

// Products by category
export const fetchProductsByCategory = () => request<any[]>('/api/products/by-category');

// POs
export const fetchOpenPOs = () => request<any>('/api/pos/open')  /* SPEC-497: {lines, state} when no live PO feed */;

// Internal transfers (SPEC-188) — read-only shadow surface, internal tiers only.
// SPEC-476: the whole reply, so a feed that has never synced (`message`, no table yet) is not
// shown as "no internal transfers".
export const fetchInternalTransfers = () =>
  request<{ items: any[]; total: number; message?: string }>('/api/portal/internal-transfers');

// SPEC-328: in-flight transfers for one SKU, for the Current Stock tile hover.
// Reuses the SAME SPEC-188 endpoint (no new backend read) — in_flight_only keeps the
// set to unreceived moves (the ones worth surfacing) and small, so the client-side
// most-recent pick is not truncated. Server does an ILIKE search, so we filter to the
// EXACT product_code client-side. Display-only.
export const fetchInternalTransfersForSku = (productCode: string) =>
  request<{ items: any[]; total: number }>(
    `/api/portal/internal-transfers?search=${encodeURIComponent(productCode)}&in_flight_only=true&limit=50`
  ).then((r) => (r.items || []).filter((t) => t.product_code === productCode));

// Buying windows
export const fetchBuyingWindows = () => request<any[]>('/api/buying-windows');

// Customer top SKUs
export const fetchCustomerTopSKUs = (code: string) => request<any[]>(`/api/customers/${code}/top-skus`);

// PO Exposure
export const fetchPOExposure = () => request<any>('/api/pos/exposure')  /* SPEC-497: {rows: null, state} when no live PO feed */;

// Overstock matches
export const fetchOverstockMatches = () => request<any[]>('/api/stock/overstock-matches');

// News
// SPEC-460: an envelope, not a bare array. `outcome` is ok | no_matches | unreachable,
// so a page can tell "nothing was relevant" apart from "the feed is down". There is no
// sample fallback — an empty `items` is a real state and must be rendered as one.
//
// SPEC-542: each item also carries `week`, `overview`, `overview_basis` and `url`. With no
// week this is the live view (the feeds now + this week's earlier stored stories); with a
// week it is that stored week, read from storage only (`stored` | `no_items_stored`).
export type NewsOverviewBasis = 'headline_and_summary' | 'headline_only' | 'template';
export interface NewsItem {
  id: string;
  headline: string;
  source: string;
  date: string;
  week?: string;
  tags: string[];
  risk_tags: string[];
  severity: string;
  ftg_implication: string;
  implication_basis?: 'model' | 'template' | '';
  overview?: string;
  overview_basis?: NewsOverviewBasis | '';
  url?: string;
}
export interface NewsPayload {
  items: NewsItem[];
  outcome: 'ok' | 'no_matches' | 'unreachable' | 'stored' | 'no_items_stored';
  message: string;
  count: number;
  week?: string;
  live?: boolean;
  live_count?: number;
  history?: 'ok' | 'read_failed';
}
export const fetchNews = (week?: string) =>
  request<NewsPayload>(week ? `/api/news?week=${encodeURIComponent(week)}` : '/api/news');
export interface NewsWeek {
  week: string;
  items: number;
  first_published: string | null;
  last_published: string | null;
}
export const fetchNewsWeeks = () => request<NewsWeek[]>('/api/news/weeks');

// SPEC-464: the Intelligence feed register (read-only). Status uses the ratified feed
// vocabulary; `as_of` is each source's OWN last successful fetch (never shared).
export type IntelSourceStatus = 'LIVE' | 'STALE' | 'AWAITING_FEED';
export interface IntelSource {
  id: string;
  name: string;
  class: 'primary' | 'research' | 'specialist' | 'reporting';
  weight: number;
  lanes: string[];
  kind: 'rss' | 'atom' | 'api';
  url: string;
  active: boolean;
  paywalled: boolean;
  licence: string;
  owner_note: string;
  status: IntelSourceStatus;
  status_reason: string | null;
  as_of: string | null;
  last_attempt_at: string | null;
  last_attempt_outcome: 'ok' | 'empty' | 'error' | null;
  last_attempt_error: string | null;
  items_held: number;
  newest_item_published: string | null;
}
export interface IntelSourceRegister {
  sources: IntelSource[];
  stale_after_days: number;
  counts: Record<IntelSourceStatus, number>;
  job_has_run: boolean;
  last_run: {
    run_id: string; ok: number; empty: number; error: number;
    started_at: string | null; ended_at: string | null;
    new_items: number; syndicated: number; failed_sources: string[];
  } | null;
  // SPEC-469: the story-analysis run as a NAMED state — failed / expired / switched off /
  // not scheduled here never read as "nothing new". `message` is plain-language backend copy.
  analysis?: {
    state: 'disabled' | 'scheduled_off' | 'capped' | 'not_run' | 'submitted' | 'ok' | 'partial' | 'failed' | 'expired' | 'nothing_new';
    message: string;
    enabled: boolean;
    stories_analysed: number;
    last_run: {
      run_id: string; status: string; submitted_at: string | null; ended_at: string | null;
      selected: number; succeeded: number; dropped_by_cap: number;
    } | null;
  };
}
export const fetchIntelSources = () =>
  request<IntelSourceRegister>('/api/intelligence/sources');

// Priority Actions
export const fetchPriorityActions = () => request<any>('/api/briefing/daily');
/** @deprecated Use fetchPriorityActions instead */
export const fetchMorningBriefing = fetchPriorityActions;

// Proactive Insights
export const fetchProductInsights = () => request<any>('/api/insights/products');
export const fetchSalesInsights = () => request<any>('/api/insights/sales');
export const fetchSupplyChainInsights = () => request<any>('/api/insights/supply-chain');

// Calendar
export const fetchUpcomingEvents = (weeks?: number) => request<any>(`/api/calendar/upcoming?weeks=${weeks || 8}`);

// AI Agent fleet (SPEC-143) — real findings, never fabricated activity
export const fetchAgentStatus = () => request<any>('/api/agents');
// SPEC-466: the remit map, this login's remits + default tab, and the rail badge count.
export const fetchAgentRemits = () => request<any>('/api/agents/remits');
export const fetchAgentFindings = (params?: { severity?: string; agent_id?: string; limit?: number; status?: string }) => {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.severity) q.set('severity', params.severity);
  if (params?.agent_id) q.set('agent_id', params.agent_id);
  if (params?.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return request<any>(`/api/agents/findings${qs ? `?${qs}` : ''}`);
};
/** SPEC-491: a dismissal carries one fixed reason (the server refuses one without). */
export const ackAgentFinding = (
  findingId: string, action: 'acknowledge' | 'dismiss' = 'acknowledge', reason?: string,
) =>
  request<any>(`/api/agents/findings/${encodeURIComponent(findingId)}/ack`, {
    method: 'POST',
    body: JSON.stringify(action === 'dismiss' ? { action, reason } : { action }),
  });
/** SPEC-491: the screen was opened from this alert (the scoreboard's "acted"). */
export const recordFindingActed = (findingId: string) =>
  request<any>(`/api/agents/findings/${encodeURIComponent(findingId)}/acted`, { method: 'POST' });
/** SPEC-491: the outcome scoreboard — admin on the Alerts page; the same read on Stats. */
export const fetchAgentScoreboard = (days = 90) => request<any>(`/api/agents/scoreboard?days=${days}`);
export const fetchStatsAlerts = (days = 90) => request<any>(`/api/stats/alerts?days=${days}`);

// Suppliers
// SPEC-157 — Exec Daily Risk Snapshot (one call powers the exec risk lens).
export const fetchExecRiskSnapshot = () => request<any>('/api/exec/risk-snapshot');

export const fetchSuppliersOverview = () => request<any[]>('/api/suppliers/overview');
export const fetchSupplierDetail = (code: string) => request<any>(`/api/suppliers/${code}/detail`);
export const fetchSuppliersOverdue = () => request<any>('/api/suppliers/overdue')  /* SPEC-497: {lines, state} when no live PO feed */;
// SPEC-221 (G35): product-grain lead-time OF-RECORD (amendable base) + measured check.
export const fetchSuppliersLeadTimes = () =>
  request<{ products: any[]; measured_available: boolean }>('/api/suppliers/lead-times');
export const submitLeadTimeOverride = (body: {
  product_code: string; lead_time_days: number; reason?: string; notes?: string;
}) =>
  request<any>('/api/suppliers/lead-time-override', {
    method: 'POST',
    body: JSON.stringify(body),
  });
export const clearLeadTimeOverride = (productCode: string) =>
  request<any>(`/api/suppliers/lead-time-override/${encodeURIComponent(productCode)}`, {
    method: 'DELETE',
  });
// SPEC-537: the lead-time check — served lines without a lead time, what completed POs
// measure (order → ETA, planned arrival not receipt), and a per-line human apply.
export const fetchLeadTimeAudit = (filter = 'all', scope = 'all') =>
  request<any>(`/api/procurement/lead-time-audit?filter=${encodeURIComponent(filter)}&scope=${encodeURIComponent(scope)}`);
export const applyLeadTimeProposal = (productCode: string, proposalDays: number) =>
  request<any>('/api/procurement/lead-time-audit/apply', {
    method: 'POST',
    body: JSON.stringify({ product_code: productCode, proposal_days: proposalDays }),
  });
export async function downloadLeadTimeAuditCsv(filter = 'all', scope = 'all'): Promise<void> {
  const res = await requestRaw(`/api/procurement/lead-time-audit/csv?filter=${encodeURIComponent(filter)}&scope=${encodeURIComponent(scope)}`);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  triggerBlobDownload(blob, match ? match[1] : 'lead_time_check.csv');
}
export const fetchSuppliersSoleSource = () => request<any[]>('/api/suppliers/sole-source');
export const fetchSupplierAlertCount = () => request<any>('/api/suppliers/alert-count');
export const fetchSupplierOrigins = () => request<any>('/api/suppliers/origins')  /* SPEC-497: {countries: null, state} when no live PO feed */;

// Rep Performance
export const fetchRepsPerformance = () => request<any[]>('/api/reps/performance');
export const fetchRepAccounts = (rep: string) => request<any>(`/api/reps/${encodeURIComponent(rep)}/accounts`);

// Margins
export const fetchProductMargins = () => request<any[]>('/api/products/margins');
export const fetchProductMarginHistory = (code: string) => request<any[]>(`/api/products/${code}/margin-history`);

// Seasonal
export const fetchSeasonalHeatmap = () => request<any[]>('/api/seasonal/heatmap');
export const fetchSeasonalRequirements = () => request<any[]>('/api/seasonal/requirements');

// Trends
export const fetchTrendRadar = () => request<any>('/api/trends/radar');

// Forward Signals
export const fetchForwardSignals = () => request<any>('/api/briefing/forward-signals');

// Business Trends
export const fetchBusinessTrends = () => request<any>('/api/briefing/business-trends');

// Budget
export const fetchBudgetTracker = () => request<any>('/api/customers/budget-tracker');

// Proforma Pipeline
export const fetchProformaPipeline = () => request<any>('/api/proformas/pipeline');

// Weather Risk
export const fetchWeatherRisk = () => request<any>('/api/commodity/weather');

// FX Risk
export const fetchFxRiskSummary = () => request<any>('/api/fx/risk-summary');
export const fetchFxRolling12w = () => request<any>('/api/fx/rolling-12w');
export const fetchFxCorridorHistory = (pair: string, days?: number) =>
  request<any[]>(`/api/fx/corridor-history?pair=${encodeURIComponent(pair)}&days=${days || 90}`);

// Route Planning
export const fetchOrderPool = () => request<any>('/api/routes/order-pool');
export const fetchDeliveryReadiness = () => request<any>('/api/routes/delivery-readiness');
export const fetchEventDemand = () => request<any>('/api/routes/event-demand');
export const fetchRouteClusters = (windowDays = 30) =>
  request<any>(`/api/routes/clusters?window_days=${windowDays}`);

// FX Margin Impact
export const fetchFxMarginImpact = () => request<any>('/api/fx/margin-impact');

// FP&A / Reports (with date range filtering)
export const fetchRevenueSummary = (dates?: { from_date?: string | null; to_date?: string | null }) => {
  const q = new URLSearchParams();
  if (dates?.from_date) q.set('from_date', dates.from_date);
  if (dates?.to_date) q.set('to_date', dates.to_date);
  const qs = q.toString();
  return request<any>(`/api/fpa/revenue-summary${qs ? '?' + qs : ''}`);
};
export const fetchMarginSummary = (dates?: { from_date?: string | null; to_date?: string | null }) => {
  const q = new URLSearchParams();
  if (dates?.from_date) q.set('from_date', dates.from_date);
  if (dates?.to_date) q.set('to_date', dates.to_date);
  const qs = q.toString();
  return request<any>(`/api/fpa/margin-summary${qs ? '?' + qs : ''}`);
};
// SPEC-523: order-line margin. Every figure is null until order cost arrives and its basis is set.
export const fetchOrderMarginSummary = (dates?: { from_date?: string | null; to_date?: string | null }) => {
  const q = new URLSearchParams();
  if (dates?.from_date) q.set('from_date', dates.from_date);
  if (dates?.to_date) q.set('to_date', dates.to_date);
  const qs = q.toString();
  return request<any>(`/api/fpa/order-margin/summary${qs ? '?' + qs : ''}`);
};
// SPEC-543: the exec "Margin this month" tile and the Sales customer view — the same order-line
// margin (one cost of record), each with its basis and costed coverage. Null figures = not known.
export const fetchOrderMarginMonth = () => request<any>('/api/fpa/order-margin/month');
export const fetchOrderMarginCustomers = (days = 90) =>
  request<any>(`/api/fpa/order-margin/customers?days=${encodeURIComponent(String(days))}`);
export const fetchWorkingCapital = () => request<any>('/api/fpa/working-capital');
export const fetchVarianceAnalysis = () => request<any>('/api/fpa/variance-analysis');
export const fetchDebtorAging = () => request<any>('/api/fpa/debtor-aging');
export const fetchCashFlowForecast = () => request<any>('/api/fpa/cash-flow-forecast');
export interface ReportParams {
  grain?: string | null;
  from_date?: string | null;
  to_date?: string | null;
}
export const generateReport = (prompt: string, params?: ReportParams) =>
  request<any>('/api/fpa/generate-report', {
    method: 'POST',
    body: JSON.stringify({ prompt, ...(params || {}) }),  // SPEC-142 §3(11): dropped dead `format`
  });

// SPEC-141: Portfolio Intelligence — real endpoints (replace the old hardcoded arrays)
export const fetchCustomerProfitability = (dates?: { from_date?: string | null; to_date?: string | null }) => {
  const q = new URLSearchParams();
  if (dates?.from_date) q.set('from_date', dates.from_date);
  if (dates?.to_date) q.set('to_date', dates.to_date);
  const qs = q.toString();
  return request<any>(`/api/fpa/customer-profitability${qs ? '?' + qs : ''}`);
};
export const fetchCategoryPortfolio = () => request<any>('/api/fpa/category-portfolio');
export const fetchOrderToInvoice = () => request<any>('/api/fpa/order-to-invoice');
export const fetchSupplierConcentration = () => request<any>('/api/fpa/supplier-concentration');
export const fetchSeasonalCalendar = () => request<any>('/api/fpa/seasonal-calendar');

// SPEC-141: Saved reports (per-user)
export const listSavedReports = () => request<any>('/api/fpa/reports');
export const saveReport = (body: { name: string; prompt: string; params?: ReportParams; spec?: any; report_id?: string }) =>
  request<any>('/api/fpa/reports', { method: 'POST', body: JSON.stringify(body) });
export const deleteSavedReport = (id: string) =>
  request<any>(`/api/fpa/reports/${id}`, { method: 'DELETE' });
export const runSavedReport = (id: string) =>
  request<any>(`/api/fpa/reports/${id}/run`, { method: 'POST' });

// SPEC-141: server-side, provenance-stamped CSV of a report table. Returns a Blob
// (not JSON). SPEC-142 §3(9): routed through requestRaw so it inherits the SAME
// silent-token-refresh-on-401 as every JSON call (a long report session no longer
// hard-fails the export on an expired token), and shares triggerBlobDownload.
export async function downloadReportCSV(body: {
  title: string; rows: any[]; columns?: string[]; as_of?: string | null; sources?: string[];
}): Promise<void> {
  const res = await requestRaw('/api/fpa/report-csv', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `${body.title || 'report'}.csv`;
  triggerBlobDownload(blob, filename);
}

// External Intelligence
export const fetchUSDAForecasts = () => request<any>('/api/commodity/intel/usda');
export const fetchUKTariffs = () => request<any>('/api/commodity/intel/tariffs');
export const fetchGeopoliticalRisk = () => request<any>('/api/commodity/intel/geopolitical');
export const fetchExternalIntelSummary = () => request<any>('/api/commodity/intel/summary');

// Retro Billing
export const fetchRetroClaims = () => request<any[]>('/api/retro/claims');
export const fetchRetroSummary = () => request<any>('/api/retro/summary');
export const fetchRetroClaimDetail = (claimId: string) => request<any>(`/api/retro/claim/${claimId}`);
export const fetchRetroReconciliation = () => request<any>('/api/retro/reconciliation-report');

// Demand Planning
export const fetchDemandOverview = () => request<any>('/api/demand/overview');
// SPEC-090: canonical SKU working-set funnel (same source as the copilot's
// get_working_set tool) so counts on screens and in chat can never disagree.
// Note the /agent prefix (not /api) — the agent router is mounted at /agent.
export const fetchWorkingSet = () => request<any>('/agent/working-set');
export const fetchDemandForecast = (category?: string) => {
  const q = category ? `?category=${encodeURIComponent(category)}` : '';
  return request<any>(`/api/demand/forecast${q}`);
};
export const fetchDemandReplenishment = (action_gate?: string) => {
  const params = new URLSearchParams();
  if (action_gate) params.set('action_gate', action_gate);
  const qs = params.toString();
  return request<any>(`/api/demand/replenishment${qs ? '?' + qs : ''}`);
};
// SPEC-231: the ONE Planning, Scheduling & Replenishment console payload -
// exception queue + full per-SKU detail, composed from real engine outputs only.
export const fetchPsrConsole = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/demand/psr-console' + qs);
};
// SPEC-257: full-catalogue lookup (code or name) — identity + status only, no projection.
export const fetchPsrConsoleSearch = (q: string, limit = 20) =>
  request<any>('/api/demand/psr-console/search?' + new URLSearchParams({ q, limit: String(limit) }).toString());
// SPEC-257: full per-SKU detail for ANY catalogue SKU (reuses the queue's detail builder;
// returns an honest no-forecast state for SKUs with no filed forecast).
export const fetchPsrConsoleDetail = (productCode: string) =>
  request<any>('/api/demand/psr-console/detail/' + encodeURIComponent(productCode));
// SPEC-267 (G41 addendum): amend the PSR cover-band criteria. Gated server-side by the
// band_criteria_edit capability (403 otherwise); validation (critical_low < min < max <
// critical_high, all > 0) surfaces as a 400 with a plain message.
export const updateBandCriteria = (data: {
  critical_low: number;
  min: number;
  max: number;
  critical_high: number;
}) => request<any>('/api/demand/psr-console/band-criteria', {
  method: 'PUT',
  body: JSON.stringify(data),
});
// SPEC-558 (G88): the planner's customer minimum life table — days of life a delivery must
// still have, per customer code OR per channel. Read: planning tier; write/retire: the
// PO-decision capability (403 otherwise). A new entry for the same scope supersedes the old.
export const fetchCustomerMinLife = () => request<any>('/api/demand/customer-min-life');
export const saveCustomerMinLife = (data: {
  customer_code?: string;
  channel?: string;
  min_days: number;
  note?: string;
}) => request<any>('/api/demand/customer-min-life', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const retireCustomerMinLife = (id: number) =>
  request<any>(`/api/demand/customer-min-life/${id}`, { method: 'DELETE' });
// SPEC-593 (G88): one product's batches judged against the usable cut-off (90 days or the
// customer minimum, whichever is higher) and the planner's life extensions. Read: planning
// console tier; extend/withdraw: the planner tier (the amendments gate; 403 otherwise).
export const fetchShelfLifeBatches = (productCode: string) =>
  request<any>(`/api/demand/shelf-life/batches?product_code=${encodeURIComponent(productCode)}`);
export const fetchLifeExtensions = (productCode?: string, history = false) => {
  const params = new URLSearchParams();
  if (productCode) params.set('product_code', productCode);
  if (history) params.set('history', 'true');
  const qs = params.toString();
  return request<any>(`/api/demand/shelf-life/extensions${qs ? '?' + qs : ''}`);
};
export const extendBatchLife = (data: {
  product_code: string;
  batch: string;
  extended_to?: string;
  extend_days?: number;
  reason: string;
  expires_at?: string;
}) => request<any>('/api/demand/shelf-life/extend', { method: 'POST', body: JSON.stringify(data) });
export const withdrawLifeExtension = (data: { id: number; reason?: string }) =>
  request<any>('/api/demand/shelf-life/withdraw', { method: 'POST', body: JSON.stringify(data) });
// SPEC-585 (G90): each retailer's order lead. Read: the promo read tier (the run's learned
// table + the planner's entries); write/retire: the planner tier (403 otherwise). A new entry for
// the same customer supersedes the old; it applies from the next engine run.
export const fetchOrderLeads = () => request<any>('/api/promo/order-leads');
export const saveOrderLead = (data: { customer_code: string; lead_weeks: number; note?: string }) =>
  request<any>('/api/promo/order-leads', { method: 'POST', body: JSON.stringify(data) });
export const retireOrderLead = (id: number) =>
  request<any>(`/api/promo/order-leads/${id}`, { method: 'DELETE' });
// SPEC-284 (Workbench §34/§35): the merged audit trail for one SKU — who changed what,
// when and why, across every ledger, newest first. Planner/admin-gated server-side (403
// otherwise). Each record lists the §34 fields its ledger cannot supply.
export const fetchAuditTrail = (productCode: string, limit = 50) =>
  request<any>('/api/audit/trail?' + new URLSearchParams({
    product_code: productCode, limit: String(limit),
  }).toString());
export const fetchDemandSeasonalHeatmap = () => request<any>('/api/demand/seasonal-heatmap');
// SPEC-525: read-only what-if parameters. The server checks every value against a fixed list
// and answers each card on its own (a value the data cannot honour comes back as `refusal`).
export interface DemandScenarioParams {
  delay_days: number;
  supplier_scope: 'top5' | 'top10' | 'one';
  supplier_code?: string;
  spike_pct: number;
  spike_scope: 'top_categories' | 'one_category';
  category?: string;
  horizon: 'next_quarter' | 'next_13_weeks';
  product_type?: string;
}
export const fetchDemandScenarios = (p?: DemandScenarioParams) => {
  const q = new URLSearchParams();
  if (p) {
    q.set('delay_days', String(p.delay_days));
    q.set('supplier_scope', p.supplier_scope);
    if (p.supplier_scope === 'one' && p.supplier_code) q.set('supplier_code', p.supplier_code);
    q.set('spike_pct', String(p.spike_pct));
    q.set('spike_scope', p.spike_scope);
    if (p.spike_scope === 'one_category' && p.category) q.set('category', p.category);
    q.set('horizon', p.horizon);
    if (p.product_type) q.set('product_type', p.product_type);
  }
  const qs = q.toString();
  return request<any>(`/api/demand/scenarios${qs ? `?${qs}` : ''}`);
};

// Demand Planning — Enhanced (Jaggi's data)
export const fetchStockCover = (category?: string) => {
  const q = category ? `?category=${encodeURIComponent(category)}` : '';
  return request<any>(`/api/demand/stock-cover${q}`);
};
export const fetchInventoryNetting = (category?: string, limit?: number) => {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return request<any>(`/api/demand/netting${qs ? '?' + qs : ''}`);
};
export const fetchBBERisk = () => request<any>('/api/demand/bbe-risk');
export const fetchWarehouseSummary = () => request<any>('/api/demand/warehouse-summary');
export const fetchProductionCapacity = () => request<any>('/api/demand/production-capacity');
export const triggerSampleIngestion = () => request<any>('/api/pipeline/ingest-sample', { method: 'POST' });

// PO Approval (Tuesday review)
export const fetchPOProposals = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/demand/po-proposals' + qs);
};
export const fetchPORequests = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/demand/po-requests' + qs);
};
export const ingestPORequests = () =>
  request<any>('/api/demand/po-requests/ingest', { method: 'POST' });
export const submitPODecision = (data: { po_number: string; product_code: string; decision: string; notes?: string }) =>
  request<any>('/api/demand/po-decision', { method: 'POST', body: JSON.stringify(data) });

// SPEC-258: Order action v1 (Sudhin's next-action flow: Order/Delay/Hold/Expedite).
// Panel = engine qty (cases) + arrival timing + same-supplier consolidation candidates.
export const fetchOrderActionPanel = (productCode: string) =>
  request<any>('/api/demand/order-action/' + encodeURIComponent(productCode));
export const submitOrderAction = (data: {
  product_code: string;
  action: string;
  chosen_qty?: number | null;
  chosen_arrival_week?: string | null;
  consolidated_skus?: Array<{ product_code: string; qty: number | null }>;
  note?: string;
  // SPEC-279: a non-BUY action's target PO + advisory suggested ETA (from the suggestion).
  target_po_number?: string | null;
  suggested_arrival_week?: string | null;
  recommended_qty?: number | null;
  recommended_arrival_week?: string | null;
  order_by_week_index?: number | null;
  lead_time_weeks?: number | null;
  order_value?: number | null;
  decision_state?: string | null;
  // SPEC-546: the panel's save key — a repeat returns the first save, never a second order.
  idempotency_key?: string;
}) => request<any>('/api/demand/order-action', { method: 'POST', body: JSON.stringify(data) });
// Order-action recommendations + amendment audit chain (rendered in PO approval).
export const fetchOrderActions = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/demand/order-actions' + qs);
};
// SPEC-538: every line the planner raised (orders + lines added to them + delay/hold/... records),
// newest first, never narrowed by a product focus — and the same list as a CSV for the buyers.
export const fetchOrdersRaised = () => request<any>('/api/demand/orders-raised');
export async function downloadOrdersRaisedCsv(): Promise<void> {
  const res = await requestRaw('/api/demand/orders-raised/export');
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  triggerBlobDownload(blob, match ? match[1] : 'orders-raised.csv');
}
// SPEC-259 (H2): the approver acts on a recommendation (approve / query / hold).
export const submitOrderActionDecision = (
  actionId: string,
  data: { decision: 'approved' | 'queried' | 'held'; notes?: string },
) => request<any>('/api/demand/order-action/' + encodeURIComponent(actionId) + '/decision',
  { method: 'POST', body: JSON.stringify(data) });

// Demand Schedule (proactive ordering)
export const fetchDemandSchedule = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/demand/schedule' + qs);
};
// SPEC-179.4: one atomic 'have / need / act next' response per SKU — replaces the
// browser-side schedule+netting join with a single backend contract. Every number
// (demand, open POs, stock, canonical recommended order qty) is sourced server-side.
export const fetchPlanningActions = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/demand/planning-actions' + qs);
};
// SPEC-212: the open-PO lines behind ONE schedule row's inbound numbers — fetched
// on row expansion. Reconciles with the row's overdue/unscheduled/inbound totals
// (same shared open-PO source as the summary).
export const fetchPlanningActionInbound = (productCode: string) =>
  request<any>(`/api/demand/planning-actions/${encodeURIComponent(productCode)}/inbound`);
// SPEC-271: planner override on ONE PO's supply treatment — exclude it from the
// calculation and/or set the week the planner assumes it lands. Append-only, audited;
// the ETA-of-record is never mutated. Reaches BOTH the console cover engine and the
// /demand/schedule feed (shared reader), so screen + copilot stay coherent.
export const setPoSupplyOverride = (body: {
  po_number: string; product_code?: string; excluded?: boolean; assumed_week?: string; reason?: string;
}) => request<any>('/api/demand/schedule/po-exclusions', { method: 'POST', body: JSON.stringify(body) });
export const restorePoSupplyOverride = (poNumber: string) =>
  request<any>(`/api/demand/schedule/po-exclusions/${encodeURIComponent(poNumber)}`, { method: 'DELETE' });
export const fetchDemandBuyers = () => request<any>('/api/demand/buyers');
// Deficit projection (SPEC-129 — forecast-driven, G13 option B)
export const fetchDeficitProjection = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/demand/deficit-projection' + qs);
};
// SPEC-223 — aggregate deficit summary for the supply-chain overview lens (tiles 1/5/7/8).
export const fetchDeficitSummary = () => request<any>('/api/demand/deficit-projection/summary');
// SPEC-230 — the full paginated list behind ONE horizon count (Dan's burn-down band pages).
// SPEC-441: `band` also takes 'all' (every line inside the widest horizon).
export const fetchDeficitBand = (band: number | string, params?: Record<string, string>) => {
  const qs = new URLSearchParams({ band: String(band), ...(params || {}) }).toString();
  return request<any>('/api/demand/deficit-projection/band?' + qs);
};
// SPEC-228 — Sudhin's action lens: five Top-SKU worklists in one payload + per-SKU dismiss.
export const fetchActionLensSummary = () => request<any>('/api/demand/action-lens/summary');
export const dismissActionLensItem = (worklist: string, productCode: string) =>
  request<any>('/api/demand/action-lens/dismiss', {
    method: 'POST',
    body: JSON.stringify({ worklist, product_code: productCode }),
  });

export const fetchDemandClassification = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/demand/classification' + qs);
};

// SPEC-191 - gross packaging (counts) & raw-material (kg) requirements over the horizon.
export const fetchMaterialRequirements = (weeks: number) =>
  request<any>('/api/demand/material-requirements?weeks=' + encodeURIComponent(String(weeks)));

// SPEC-408B - per-RM usage forecast (Engine 2A): FG-driven + direct + strategic RM usage,
// weekly/monthly/quarterly/annual, seasonality; cover + spend are honest-blocked (no RM stock/price feed).
export const fetchUsageForecast = (rmCode: string, weeks: number) =>
  request<any>('/api/demand/usage-forecast/' + encodeURIComponent(rmCode) +
    '?weeks=' + encodeURIComponent(String(weeks)));

// SPEC-408C - per-RM netting table (Engine 3): Requirement real (BOM explosion); opening/incoming/
// closing/cover + Firm/Expected/Potential supply are honest-blocked until the RM stock + PO feed lands.
export const fetchMaterialNettingRm = (rmCode: string, weeks: number) =>
  request<any>('/api/demand/material-netting/' + encodeURIComponent(rmCode) +
    '?weeks=' + encodeURIComponent(String(weeks)));
// SPEC-408E - per-RM scheduling timeline (Engine 6): production + requirement tracks real,
// call-off / PO / delivery / readiness / milestone tracks honest-blocked (awaiting the PO feed).
export const fetchRmSchedule = (rmCode: string, weeks: number) =>
  request<any>('/api/demand/schedule/' + encodeURIComponent(rmCode) + '?weeks=' + encodeURIComponent(String(weeks)));
// SPEC-408F - right-rail intelligence + audit KPIs for the Material Planning Console.
export const fetchConsoleIntel = (rmCode: string, weeks: number) =>
  request<any>('/api/demand/console-intel/' + encodeURIComponent(rmCode) +
    '?weeks=' + encodeURIComponent(String(weeks)));
// SPEC-408F - portfolio-wide Engine-11 audit KPIs (RM-independent) for the console bottom bar.
export const fetchConsoleAuditKpis = () =>
  request<any>('/api/demand/console-audit-kpis');

// SPEC-399 - read-only preview of the new-product analogue match (basket, confidence, why).
export const fetchAnaloguePreview = (productCode: string, basketSize?: number) =>
  request<any>('/api/demand/analogue-preview/' + encodeURIComponent(productCode) +
    (basketSize ? '?basket_size=' + encodeURIComponent(String(basketSize)) : ''));

// SPEC-446 - New-Product Matching: the searchable picker behind "choose a different
// match", plus the planner override (record / reverse). The override is an
// amendment-class act, so both writes are audit-trailed server-side.
export const fetchAnalogueCandidates = (query: string, limit = 20) =>
  request<any>('/api/demand/analogue-candidates?q=' + encodeURIComponent(query) +
    '&limit=' + encodeURIComponent(String(limit)));

export const setAnalogueOverride = (
  productCode: string, analogueCode: string, reason?: string,
) =>
  // No `headers` here on purpose: requestRaw sets Content-Type AND the bearer token,
  // then spreads these options OVER them — passing headers would drop the token.
  request<any>('/api/demand/analogue-override/' + encodeURIComponent(productCode), {
    method: 'POST',
    body: JSON.stringify({ analogue_code: analogueCode, reason: reason || null }),
  });

export const clearAnalogueOverride = (productCode: string) =>
  request<any>('/api/demand/analogue-override/' + encodeURIComponent(productCode), {
    method: 'DELETE',
  });

// SPEC-406 - inventory / stock-targets: cover-band summary + per-SKU cover targets.
export const fetchInventorySummary = () =>
  request<any>('/api/inventory/summary');

export const fetchInventoryTargets = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/inventory/targets' + qs);
};

// SPEC-407 - forecast benchmark: the ERP's buy report with our forecast numbers per product.
export const fetchDeficitReport = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/portal/deficit-report' + qs);
};

// SPEC-417d - deductions: promo participation (real) + awaiting-feed status.
export const fetchDeductionsParticipation = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/deductions/participation' + qs);
};

export const fetchDeductionsFeedStatus = () =>
  request<any>('/api/deductions/feed-status');

// SPEC-411 (scaffold) - replenishment detail, regime alerts, customer dormancy: fetch
// helpers only (the 3 pages are stubs; a later slice wires these in).
export const fetchReplenishmentSignals = () =>
  request<any>('/api/replenishment/signals');

export const fetchReplenishmentDetail = (productCode: string) =>
  request<any>('/api/replenishment/signals/' + encodeURIComponent(productCode));

// SPEC-455 — the demand-source comparison (the legacy aggregate vs the published Box A book).
// Read-only diagnostics: these report WHICH source the platform's demand-of-record reads today
// and how the two have compared day by day. Nothing here flips the source — that is a recorded
// Param+Sudhin sign-off (G69), deliberately not an API call.
export const fetchDemandSourceStatus = () =>
  request<any>('/api/demand-source/status');

export const fetchDemandSourceComparison = (params?: { snapshot_date?: string; limit?: number }) => {
  const q = new URLSearchParams();
  if (params?.snapshot_date) q.set('snapshot_date', params.snapshot_date);
  if (params?.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return request<any>(`/api/demand-source/comparison${qs ? `?${qs}` : ''}`);
};

export const fetchDemandSourceHistory = (days = 30) =>
  request<any>(`/api/demand-source/history?days=${encodeURIComponent(String(days))}`);

// SPEC-541 — Recommendations and outcomes (read-only). The nightly run records every suggestion,
// matches what the team did and judges it once its window has passed. Nothing here writes; a
// lead-time proposal is applied through the existing lead-time amendment (submitLeadTimeOverride).
export type RecommendationFilters = {
  weeks?: number; basis?: 'recorded' | 'all'; week?: string; rec_type?: string;
  category?: string; status?: string; outcome?: string; product?: string;
  limit?: number; offset?: number;
};
export function recommendationQuery(f: RecommendationFilters = {}): string {
  const q = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}
export const fetchRecommendationSummary = (weeks = 8, basis: 'recorded' | 'all' = 'recorded') =>
  request<any>(`/api/recommendations/summary${recommendationQuery({ weeks, basis })}`);
export const fetchRecommendationLines = (f: RecommendationFilters) =>
  request<any>(`/api/recommendations/lines${recommendationQuery(f)}`);
export const fetchRecommendationProposals = () =>
  request<any>('/api/recommendations/proposals');
// SPEC-594: the buying-decisions strip (Demand Planning › Measure › Forecast vs Actuals).
export const fetchRecommendationDecisions = (weeks = 26) =>
  request<any>(`/api/recommendations/decisions${recommendationQuery({ weeks })}`);

export const fetchRegimes = () =>
  request<any>('/api/forecast/regimes');

export const fetchRegimeDetail = (productCode: string) =>
  request<any>('/api/forecast/regime/' + encodeURIComponent(productCode));

export const fetchDormantAccounts = (window?: number, sort?: string) => {
  const params = new URLSearchParams();
  if (window) params.set('window', String(window));
  if (sort) params.set('sort', sort);
  const qs = params.toString();
  return request<DormantRead>('/api/dormant' + (qs ? '?' + qs : ''));
};

// Forecast — per-SKU models
export const fetchForecastAccuracy = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/forecast/accuracy' + qs);
};
// SPEC-532 — one page of the every-line final forecast (walk it with lib/finalGrid.walkFinalGrid).
export const fetchFinalGridPage = (offset: number, planningVersion: string | null) => {
  const q = new URLSearchParams({ offset: String(offset), limit: '500' });
  if (planningVersion) q.set('planning_version', planningVersion);
  return request<import('./finalGrid').FinalGridPage>(`/api/forecast/final-grid?${q.toString()}`);
};
// SPEC-536 — one line of the every-line view, opened: customers, basis, signals, why, horizon.
// Pass the grid's planning version so the customer split reads the same book.
export const fetchFinalGridLine = (code: string, planningVersion: string | null) => {
  const q = new URLSearchParams();
  if (planningVersion) q.set('planning_version', planningVersion);
  const qs = q.toString();
  return request<import('./lineDrilldown').LineDrilldown>(
    `/api/forecast/final-grid/line/${encodeURIComponent(code)}${qs ? `?${qs}` : ''}`);
};
// SPEC-590 — the forecast "With promotions": the published base, the promotion figure beside it,
// the planner's figure where amended. The full view carries no line detail; one product does.
export const fetchWithPromotions = () =>
  request<import('./withPromotions').WithPromotionsView>('/api/forecast/with-promotions');
export const fetchWithPromotionsProduct = (code: string) =>
  request<import('./withPromotions').WithPromotionsView>(
    `/api/forecast/with-promotions/product/${encodeURIComponent(code)}`);
export async function downloadWithPromotionsCsv(): Promise<void> {
  const res = await requestRaw('/api/forecast/with-promotions/csv');
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  triggerBlobDownload(blob, match ? match[1] : 'forecast_with_promotions.csv');
}
// SPEC-595 — the with-promotions scorecard (one row per product with a record).
export async function downloadWithPromotionsScorecardCsv(): Promise<void> {
  const res = await requestRaw('/api/forecast/with-promotions/scorecard.csv');
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  triggerBlobDownload(blob, match ? match[1] : 'forecast_with_promotions_scorecard.csv');
}
// SPEC-532 — line counts for the forecast-grid download's scope toggle.
export const fetchWeeklyGridScope = () =>
  request<{ all_lines: number; planner_set_lines: number | null }>('/api/forecast/weekly-grid/scope');
// SPEC-223 — grade counts only (no per-SKU rows) for the overview forecast-quality tile.
export const fetchAccuracySummary = () => request<any>('/api/forecast/accuracy/summary');
// SPEC-277 — forecast quality watchlist: consistently good/bad SKUs + spike flag (planner-tier).
export const fetchForecastWatchlist = (limit = 10) =>
  request<any>(`/api/measure/forecast-quality?limit=${limit}`);
export const fetchSkuForecast = (productCode: string, horizon?: number) =>
  request<any>(`/api/forecast/sku/${encodeURIComponent(productCode)}${horizon ? `?horizon=${horizon}` : ''}`);
// SPEC-308 — one-product rerun + governed model/analogue decisions.
export const fetchForecastControls = (productCode: string) =>
  request<any>(`/api/forecast/controls/${encodeURIComponent(productCode)}`);
export const rerunProductForecast = (productCode: string) =>
  request<any>(`/api/forecast/controls/${encodeURIComponent(productCode)}/rerun`, {
    method: 'POST', body: JSON.stringify({}),
  });
export const chooseForecastModel = (productCode: string, data: {
  model: string; reason: string; expires_at: string;
}) => request<any>(`/api/forecast/controls/${encodeURIComponent(productCode)}/model-choice`, {
  method: 'POST', body: JSON.stringify(data),
});
export const chooseForecastAnalogue = (productCode: string, data: {
  analogue_code: string; reason: string; expires_at: string;
}) => request<any>(`/api/forecast/controls/${encodeURIComponent(productCode)}/analogue`, {
  method: 'POST', body: JSON.stringify(data),
});
export const submitForecastOverride = (data: {
  product_code: string; override_qty: number; reason_code: string; notes?: string; username: string;
  // SPEC-153: single week (week_number) OR a period (week_numbers) — exactly one.
  week_number?: number; week_numbers?: number[];
}) => request<any>('/api/forecast/override', { method: 'POST', body: JSON.stringify(data) });
// weekKey is the stored key (week_key from the overrides response), not the sliding index.
export const deleteForecastOverride = (productCode: string, weekKey: number) =>
  request<any>(`/api/forecast/override/${encodeURIComponent(productCode)}/${weekKey}`, { method: 'DELETE' });
// SPEC-153: clear a whole period (list of week_key values) in one go.
export const deleteForecastOverridePeriod = (productCode: string, weekKeys: number[]) =>
  request<any>(`/api/forecast/override/${encodeURIComponent(productCode)}?${weekKeys.map(k => `weeks=${k}`).join('&')}`, { method: 'DELETE' });
export const fetchForecastOverrides = (productCode: string) =>
  request<any>(`/api/forecast/overrides/${encodeURIComponent(productCode)}`);
export const triggerBuyingDataIngestion = () =>
  request<any>('/api/pipeline/ingest-buying-data', { method: 'POST' });

// NAM Forecast (commercial expectation — comparison only, never affects final forecast)
export const submitNamForecast = (data: {
  product_code: string; week_number: number; nam_qty: number; notes?: string; username: string;
}) => request<any>('/api/forecast/nam', { method: 'POST', body: JSON.stringify(data) });
export const fetchNamForecasts = (productCode: string) =>
  request<any>(`/api/forecast/nam/${encodeURIComponent(productCode)}`);
export const deleteNamForecast = (productCode: string, weekNumber: number) =>
  request<any>(`/api/forecast/nam/${encodeURIComponent(productCode)}/${weekNumber}`, { method: 'DELETE' });

// Planner Amendments — cross-SKU view (Governance Section 10)
export const fetchAllAmendments = (activeOnly: boolean = true) =>
  request<any>(`/api/forecast/overrides/all?active_only=${activeOnly}`);

// SPEC-226 (M5): read-only aggregate for the Overview/team-lens amendment tiles — the
// by_planner/today/yesterday/active counts ONLY, with no expiry-sweep write and no
// per-amendment row payload. Row-level consumers (Morning View, the amendments board,
// governance, the persona expiring tile) keep fetchAllAmendments.
export const fetchAmendmentsSummary = () =>
  request<any>(`/api/forecast/overrides/summary`);

// Demand Requests (SPEC-186) — tracked demand commentary that lands in the NAM stream.
// File is open to any non-viewer; deciding is planner-tier; reads are open.
export const fileDemandRequest = (data: {
  product_code: string; volume: number; period_type: 'month' | 'week';
  period_value: string; commentary?: string;
}) => request<any>('/api/demand-requests', { method: 'POST', body: JSON.stringify(data) });
export const fetchDemandRequests = (opts?: { product_code?: string; status?: string }) => {
  const qs = new URLSearchParams();
  if (opts?.product_code) qs.set('product_code', opts.product_code);
  if (opts?.status) qs.set('status', opts.status);
  const q = qs.toString();
  return request<any>(`/api/demand-requests${q ? `?${q}` : ''}`);
};
export const fetchDemandRequest = (id: number) =>
  request<any>(`/api/demand-requests/${id}`);
// SPEC-288: planner-tier open-request count for the sidebar badge (403 for non-planner).
export const fetchDemandRequestPendingCount = () =>
  request<{ pending_count: number }>(`/api/demand-requests/pending-count`);
export const decideDemandRequest = (id: number, data: {
  decision: 'accept' | 'deny'; reason_code: string; notes?: string; accepted_volume?: number;
}) => request<any>(`/api/demand-requests/${id}/decide`, { method: 'POST', body: JSON.stringify(data) });

// ROS 12-Week Safeguard (Governance Section 12)
export const fetchRosSafeguard = (productCode: string) =>
  request<any>(`/api/forecast/ros/${encodeURIComponent(productCode)}`);

// Pipeline Runs (SPEC-017 — run-based versioning)
export const fetchPipelineRuns = (limit: number = 20) =>
  request<any>(`/api/pipeline/runs?limit=${limit}`);
export const fetchPipelineRunDetail = (runId: string) =>
  request<any>(`/api/pipeline/runs/${encodeURIComponent(runId)}`);

// Bias Drift Monitoring (SPEC-018 — Governance Section 6.2)
export const fetchBiasDrift = () => request<any>('/api/forecast/bias-drift');
export const triggerBiasDriftCheck = () =>
  request<any>('/api/forecast/bias-drift/check', { method: 'POST' });

// Decision Ledger (SPEC-019 — Governance audit trail)
export const fetchDecisionLedger = (params?: { run_id?: string; product_code?: string; decision_type?: string; limit?: number }) => {
  const qp = new URLSearchParams();
  if (params?.run_id) qp.set('run_id', params.run_id);
  if (params?.product_code) qp.set('product_code', params.product_code);
  if (params?.decision_type) qp.set('decision_type', params.decision_type);
  if (params?.limit) qp.set('limit', String(params.limit));
  return request<any>(`/api/demand/decisions?${qp.toString()}`);
};
export const fetchDecisionImpact = () => request<any>('/api/demand/decisions/impact');

// Extended Model Trials (SPEC-020 — Governance Section 7)
export const fetchExtendedModelTrials = () => request<any>('/api/forecast/extended-models');

// Forecast Explainability (SPEC-031 — Planner transparency)
export const fetchForecastExplain = (code: string) =>
  request<any>(`/api/forecast/explain/${encodeURIComponent(code)}`);

// Product Status Tracking (SPEC-055/056)
export const fetchProductStatusHistory = (code: string) =>
  request<any>(`/api/products/${encodeURIComponent(code)}/status-history`);
export const fetchProductAvailabilitySummary = () =>
  request<any>(`/api/products/availability-summary`);

// Pilot Measurement Packs (V6.2/V6.3 — scoped to 250 pilot SKUs)
export const fetchPilotDailyPack = () => request<any>('/api/demand/ops/daily-pack');
export const fetchPilotWeeklyScorecard = () => request<any>('/api/demand/ops/weekly-scorecard');
export const fetchPilotSkus = () => request<any>('/api/demand/ops/skus');
export const fetchPilotCustomers = () => request<any>('/api/demand/ops/customers');
export const fetchModelPerformance = () => request<any>('/api/demand/ops/model-performance');

// Segment drill-down
export const fetchSkuSegmentBreakdown = (productCode: string) =>
  request<any>(`/api/demand/sku/${encodeURIComponent(productCode)}/segment-breakdown`);

// Calculations tab
export const fetchForecastCalculations = (productCode: string) =>
  request<any>(`/api/forecast/calculations/${encodeURIComponent(productCode)}`);
export const searchCustomersForCalc = (q: string) =>
  request<any>(`/api/demand/customers/search?q=${encodeURIComponent(q)}`);
export const fetchCustomerCalcProfile = (customerCode: string) =>
  request<any>(`/api/demand/customer/${encodeURIComponent(customerCode)}/profile`);

// Scenario suggestions: retired (SPEC-521). The Scenarios page files them through the
// feedback panel (lib/feedback/api.ts) and lists them from /api/feedback/mine.

// Promotional calendar
export const fetchPromotions = (filters?: { status?: string; category?: string; customer?: string; product_code?: string; product_type?: string; scope?: string; limit?: number }) => {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.customer) params.set('customer', filters.customer);
  if (filters?.product_code) params.set('product_code', filters.product_code);
  if (filters?.product_type) params.set('product_type', filters.product_type);
  if (filters?.scope) params.set('scope', filters.scope);
  if (filters?.limit != null) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return request<any>(`/api/demand/promotions${qs ? '?' + qs : ''}`);
};
// SPEC-364: the SAME selection, grouped BY SKU on the server. The by-promotion endpoint
// caps promotions, so a SKU's lines could split across the cap; this one caps SKUs, and
// every SKU it returns carries every line it has in the requested window.
export const fetchPromotionsBySku = (filters?: { status?: string; category?: string; customer?: string; product_code?: string; product_type?: string; scope?: string; limit?: number }) => {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.customer) params.set('customer', filters.customer);
  if (filters?.product_code) params.set('product_code', filters.product_code);
  if (filters?.product_type) params.set('product_type', filters.product_type);
  if (filters?.scope) params.set('scope', filters.scope);
  if (filters?.limit != null) params.set('limit', String(filters.limit));
  const qs = params.toString();
  return request<any>(`/api/demand/promotions/by-sku${qs ? '?' + qs : ''}`);
};
export const fetchSkuPromotions = (productCode: string) =>
  request<any>(`/api/demand/promotions/sku/${encodeURIComponent(productCode)}`);
export const fetchPromoSummary = (scope: string = 'current') =>
  request<any>(`/api/demand/promotions/summary?scope=${encodeURIComponent(scope)}`);

// XGBoost sensing status
export const fetchSensingStatus = () => request<any>('/api/forecast/sensing/status');

// Decision Bridge / Regime Classification / ML Governance (Pilot)
export const fetchDecisionBridge = () => request<any>('/api/demand/ops/decision-bridge');
export const fetchRegimeClassification = () => request<any>('/api/demand/ops/regime-classification');
export const fetchMLGovernance = () => request<any>('/api/demand/ops/ml-governance');
// SPEC-169 (G15-C): regime-routed buy MEASUREMENT — counterfactual impact (planner tier).
export const fetchRegimeMeasurement = () => request<any>('/api/forecast/regime-measurement');

// Chat suggestions
export const fetchChatSuggestions = (page: string) => request<any>(`/agent/suggestions?page=${encodeURIComponent(page)}`);

// Anomaly Explanation
export const explainAnomaly = (entityType: string, entityCode: string, anomalyDescription: string) =>
  request<{ explanation: string; action: string }>('/agent/explain-anomaly', {
    method: 'POST',
    body: JSON.stringify({ entity_type: entityType, entity_code: entityCode, anomaly_description: anomalyDescription }),
  });

// Agent
// SPEC-108: `mode: 'planner'` opts into the explainability copilot (server role-gates it;
// non-planning accounts silently fall back to the general chatbot).
export const sendAgentMessage = (
  messages: { role: string; content: string }[],
  conversationId?: string,
  mode?: 'planner',
) =>
  request<any>('/agent/chat', {
    method: 'POST',
    body: JSON.stringify({ messages, conversation_id: conversationId, mode }),
  });

// SPEC-131: the server's own planner-mode verdict for this account. The chat
// drawer reads `planner_authorized` instead of re-implementing the role check in
// TS, so the UI toggle and the backend gate (_planner_authorized) can never drift.
export const fetchAgentCapabilities = () =>
  request<{ planner_authorized: boolean; planner_roles: string[]; planner_capability: string }>(
    '/agent/capabilities',
  );

// Legacy agent (fallback)
export const askAgent = (question: string) =>
  request<any>('/agent/ask', {
    method: 'POST',
    body: JSON.stringify({ question }),
  });

// Email composer
export const composeEmail = (customerCode: string, customerName: string, context: string) =>
  request<{ subject: string; body: string }>('/agent/compose-email', {
    method: 'POST',
    body: JSON.stringify({ customer_code: customerCode, customer_name: customerName, context }),
  });

// Governance
export const fetchGovernanceDataSources = () => request<any>('/api/governance/data-sources');
export const fetchGovernanceAlgorithms = () => request<any>('/api/governance/algorithms');
export const fetchGovernanceFreshness = () => request<any>('/api/governance/freshness');
export const fetchGovernanceOperations = () => request<any>('/api/governance/operations');
// SPEC-486: layer 1 (releases, one month per page) for every login; layer 2 (the commits
// behind one release or one summarised month) is admin-only on the server.
export const fetchGovernanceChangelog = (month?: string) =>
  request<any>(`/api/governance/changelog${month ? `?month=${encodeURIComponent(month)}` : ''}`);
export const fetchChangelogDetail = (q: { release?: string; month?: string; offset?: number }) => {
  const p = new URLSearchParams();
  if (q.release) p.set('release', q.release);
  if (q.month) p.set('month', q.month);
  if (q.offset) p.set('offset', String(q.offset));
  return request<any>(`/api/governance/changelog/detail?${p.toString()}`);
};
// SPEC-100: canonical governance constants (WAPE/bias/gate/ADI/cover bands).
export const fetchGovernanceConstants = () => request<any>('/api/governance/constants');
// SPEC-128 (G18): pilot phase gates + rollback triggers.
export const fetchGovernancePilotPhases = () => request<any>('/api/governance/pilot-phases');
export const transitionPilotPhase = (data: { target_phase: number; note: string }) =>
  request<any>('/api/governance/pilot-phases/transition', { method: 'POST', body: JSON.stringify(data) });
export const setPilotCriterion = (data: { criterion_key: string; checked: boolean; note: string }) =>
  request<any>('/api/governance/pilot-phases/criterion', { method: 'POST', body: JSON.stringify(data) });
export const fetchGuardrailViolations = (params?: { severity?: string; rule?: string; product_code?: string; limit?: number; offset?: number }) => {
  const q = new URLSearchParams();
  if (params?.severity) q.set('severity', params.severity);
  if (params?.rule) q.set('rule', params.rule);
  if (params?.product_code) q.set('product_code', params.product_code);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.offset) q.set('offset', String(params.offset));
  const qs = q.toString();
  return request<any>(`/api/governance/guardrails${qs ? '?' + qs : ''}`);
};

// Automation Target Tracking (SPEC-021 — Governance Section 16)
export const fetchAutomationTargets = () => request<any>('/api/governance/automation-targets');

// SPEC-046/047/048: Confidence, Risk, and Classification Audit
export const fetchConfidenceDistribution = () => request<any>('/api/governance/confidence-distribution');
export const fetchRiskDistribution = () => request<any>('/api/governance/risk-distribution');
export const fetchClassificationAudit = () => request<any>('/api/demand/classification/audit-summary');
// SPEC-112: per-SKU reclassification history ("why did this change class?")
export const fetchClassificationHistory = (productCode: string) =>
  request<any>(`/api/demand/classification/history/${encodeURIComponent(productCode)}`);
// SPEC-405: per-SKU forecast health report ("why does this forecast look like this?")
export const forecastDiagnose = (productCode: string) =>
  request<any>(`/api/forecast/diagnose/${encodeURIComponent(productCode)}`);
// SPEC-517 (G62): how a product's weekly forecast moved, publication by publication.
export interface PublicationHistoryRow {
  published_at: string | null;
  published_on: string | null;
  basis_week: string;
  year_week: string;
  forecast: number | null;
  base: number | null;
  promo_uplift: number | null;
  other_signals: number | null;
  signals: string | null;
  demand_class: string | null;
  box_a_demand_of_record: number | null;
  model_name: string | null;
  train_end_week: string | null;  // SPEC-531: 'YYYY-WNN' the model was fitted on data to
}
export interface PublicationHistory {
  product_code: string;
  year_week: string | null;
  cadence: string | null;
  message: string | null;
  last_published: string | null;
  next_publication: string | null;
  publications: number;
  rows: PublicationHistoryRow[];
}
export const fetchForecastPublicationHistory = (productCode: string, yearWeek?: string) =>
  request<PublicationHistory>(
    `/api/forecast/history?product_code=${encodeURIComponent(productCode)}`
    + (yearWeek ? `&year_week=${encodeURIComponent(yearWeek)}` : ''),
  );

// SPEC-111: governance surface truth — coherence, FVA trend, tool registry, provenance extras
export const fetchGovernanceCoherence = (fresh: boolean = false) =>
  request<any>(`/api/governance/coherence${fresh ? '?fresh=1' : ''}`);
export const fetchGovernanceFvaTrend = (weeks: number = 12) =>
  request<any>(`/api/governance/fva-trend?weeks=${weeks}`);
export const fetchGovernanceProvenanceExtras = () => request<any>('/api/governance/provenance-extras');
// SPEC-198: the living data-architecture map (curated flow graph + live freshness)
export const fetchGovernanceArchitectureMap = () => request<any>('/api/governance/architecture-map');

/* SPEC-448: the observability payloads. `pipeline` is the same curated map PLUS a health
   object, a blast radius and the source-fault register per node; `health` is the rollup
   alone. The four health states are ranked: unknown < healthy < caution < degraded. */
export type HealthState = 'unknown' | 'healthy' | 'caution' | 'degraded';
export type FeedVerdict = 'LIVE' | 'STALE' | 'DEAD_LEGACY' | 'AWAITING_FEED';

export interface ObservabilityCheck {
  family: string;
  label: string;
  state: HealthState;
  reason: string;
  observed: any;
  expected: any;
  severity: 'WARN' | 'ERROR';
  blocking: boolean;
  attribution: 'OURS' | 'SOURCE';
}

export interface SourceFault {
  id: string;
  summary?: string;
  owner?: string;
  priority?: string;
  status?: string;
  attribution?: string;
  open?: boolean;
  tables?: string[];
  columns?: string[];
}

export interface NodeHealth {
  state: HealthState;
  reason: string;
  measured: boolean;
  as_of: string | null;
  row_count: number | null;
  feed_status: FeedVerdict | null;
  last_run: Record<string, any> | null;
  checks: ObservabilityCheck[];
  source_faults: SourceFault[];
  upstream_degraded: string[];
  upstream_flagged: string[];
}

export const fetchObservabilityPipeline = (trendDays: number = 7) =>
  request<any>(`/api/observability/pipeline?trend_days=${encodeURIComponent(String(trendDays))}`);
export const fetchObservabilityHealth = () => request<any>('/api/observability/health');
// SPEC-304: delivery roadmap — read-only milestone list, all logged-in roles.
export type MilestoneStatus = 'delivered' | 'in_progress' | 'next' | 'awaiting_input';
/** SPEC-453 — HOW the status is known, resolved per request by the backend.
 *  `confirmed`/`contradicted` = a mechanical check ran; `reviewed` = a person last checked
 *  it on `reviewed`; `unreviewed` = neither, stated rather than hidden. */
export type MilestoneBasis = 'confirmed' | 'contradicted' | 'reviewed' | 'unreviewed';
export interface Milestone {
  id: string;
  title: string;
  description: string;
  status: MilestoneStatus;
  owner: 'us' | 'them';
  date?: string | null;
  status_basis?: MilestoneBasis;
  /** ISO date a person last checked this row. Present when `status_basis` is 'reviewed'. */
  reviewed?: string | null;
}
export interface RoadmapPayload {
  milestones: Milestone[];
  updated?: string | null;
}
export const fetchRoadmapMilestones = () => request<RoadmapPayload>('/api/roadmap/milestones');

// SPEC-453: the term meanings the screen shows, from the ONE glossary the copilot reads
// (backend/agent_glossary.py). Nothing here defines a meaning — a term absent from this
// payload shows no ⓘ at all rather than a second, drifting definition.
export interface GlossaryTerm {
  term: string;
  /** Lookup keys. An honest-state pill finds its entry by the EXACT state token. */
  aliases: string[];
  definition: string;
  on_screen?: string | null;
  /** Names the open ruling when the meaning is still an interim default. */
  pending_ruling?: string | null;
}
export interface GlossaryPayload {
  terms: GlossaryTerm[];
  count: number;
}
export const fetchGlossary = () => request<GlossaryPayload>('/api/governance/glossary');

/* SPEC-457 — per-forecast thumbs. A verdict is recorded for the weekly review and changes
 * NO number: no recompute, no plan change, no notification. The response is deliberately
 * plain so a caller cannot render it as a promise of action. */
export type FeedbackVerdict = 'up' | 'down';
/** v1 grain: a SKU forecast figure, or a Box A grain panel figure. SPEC-491 adds an
 *  Alerts-page row ("is this alert accurate, given the data?"). */
export type FeedbackEntityType = 'sku_forecast' | 'box_a_grain' | 'agent_finding';
export interface FeedbackSubmission {
  entity_type: FeedbackEntityType;
  entity_id: string;
  verdict: FeedbackVerdict;
  sku_code?: string;
  /** Only kept on a thumbs-DOWN; the server discards it on a thumbs-up. */
  comment?: string;
}
export interface FeedbackResult {
  entity_type: string;
  entity_id: string;
  period: string;
  verdict: FeedbackVerdict;
  comment: string;
  outcome: 'created' | 'updated';
}
export const submitForecastFeedback = (body: FeedbackSubmission) =>
  request<FeedbackResult>('/api/forecast/feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export interface MyFeedbackPayload {
  enabled: boolean;
  /** FALSE when the server could not read the table. A fault is not "you have no
   *  verdicts" — the caller must retry rather than render every figure as unrated. */
  verdicts_available?: boolean;
  /** The forecast cycle these verdicts belong to. Null when no cycle is established. */
  period: string | null;
  /** entity_id -> this user's own verdict. Never anybody else's (SPEC-457: private
   *  until the digest, so no one's amendment is anchored on peer sentiment).
   *  `period` is the cycle THAT figure belongs to — per SKU, because a partial refit
   *  leaves a mix of anchors, so a page-wide period would misname it. */
  verdicts: Record<string, { verdict: FeedbackVerdict; comment: string; period?: string }>;
}
export const fetchMyForecastFeedback = (entityType: FeedbackEntityType) =>
  request<MyFeedbackPayload>(
    `/api/forecast/feedback/mine?entity_type=${encodeURIComponent(entityType)}`);
// SPEC-205: per-screen readiness — which feeds each screen leans on, whether they're live,
// and what anything not-yet-open is waiting for. Powers the readiness board + per-page chip.
export type ReadinessLevel = 'verified' | 'live_unverified' | 'partial' | 'awaiting_feed' | 'in_development';
export interface SurfaceFeed {
  id: string;
  label: string;
  freshness: { last_updated: string; days_ago: number | null } | null;
}
export interface Surface {
  route: string;
  label: string;
  readiness: ReadinessLevel;
  unlock?: string;
  feeds: SurfaceFeed[];
}
export interface SurfaceReadinessPayload {
  readiness_levels: Record<string, string>;
  surfaces: Surface[];
}
export const fetchSurfaceReadiness = () => request<SurfaceReadinessPayload>('/api/governance/surface-readiness');
export const fetchForecastFva = (limit: number = 50) =>
  request<any>(`/api/forecast/fva?limit=${limit}`);

// ── Stats surface (SPEC-426) — super_admin-only self-analytics / ROI observatory.
// Typed against the §5 contract; the router ships in a sibling slice. Each board
// guards for absence so a not-yet-live endpoint renders "awaiting data", not a crash.
export const fetchStatsHero = () => request<HeroPayload>('/api/stats/hero');
export const fetchStatsLive = () => request<LivePayload>('/api/stats/live');
export const fetchStatsUsage = (days?: number) =>
  request<UsagePayload>(`/api/stats/usage${days ? `?days=${days}` : ''}`);
export const fetchStatsRoi = () => request<RoiPayload>('/api/stats/roi');
export const fetchStatsRoiAssumptions = () => request<RoiAssumptions>('/api/stats/roi/assumptions');
export const saveStatsRoiAssumptions = (body: AssumptionsBody | CostOfBuildBody) =>
  request<RoiAssumptions>('/api/stats/roi/assumptions', { method: 'PUT', body: JSON.stringify(body) });
export const fetchStatsBuilder = () => request<BuilderPayload>('/api/stats/builder');
// SPEC-483: AI usage — every logged model call (llm_calls), £ as an estimate.
export const fetchStatsAi = (days?: number) =>
  request<AiPayload>(`/api/stats/ai${days ? `?days=${days}` : ''}`);

// Analytics & Layout
// SPEC-252: usage-summary / roi-summary / roi-config / user/{username} endpoints and
// their wrappers were removed with the Usage/User-Activity/ROI/Config admin tabs.
export const fetchAnalyticsSessions = (days?: number) => request<any>(`/api/analytics/sessions?days=${days || 30}`);
export const trackAnalyticsEvent = (eventType: string, page: string, eventData: string = '') =>
  request<any>('/api/analytics/events', {
    method: 'POST',
    body: JSON.stringify({ event_type: eventType, page, event_data: eventData }),
  });
export const fetchUserLayout = (page: string) => request<any>(`/api/user/layout/${encodeURIComponent(page)}`);
export const saveUserLayout = (page: string, widgetOrder: string[]) =>
  request<any>(`/api/user/layout/${encodeURIComponent(page)}`, {
    method: 'PUT',
    body: JSON.stringify({ widget_order: widgetOrder }),
  });
export const resetUserLayout = (page: string) =>
  request<any>(`/api/user/layout/${encodeURIComponent(page)}`, { method: 'DELETE' });

// SPEC-452 — per-user pinned nav pages (the sidebar Favourites group). The server keys
// the row on the JWT username, so no user id travels on the wire. PUT replaces the whole
// list; the response echoes the stored list after validation.
export const fetchNavFavourites = () => request<{ pages: string[] }>('/api/user/nav-favourites');
export const saveNavFavourites = (pages: string[]) =>
  request<{ ok: boolean; pages: string[] }>('/api/user/nav-favourites', {
    method: 'PUT',
    body: JSON.stringify({ pages }),
  });

// SPEC-563 — the per-user sidebar layout (folders, order, hidden screens, favourites folded
// in). Keyed on the JWT username server-side. The PUT is compare-and-set: it carries the
// version the client last saw, and a stale one is a 409 whose ApiError.body holds the
// current { layout, version }. The model and its rules live in config/nav-layout.ts.
export interface NavLayoutWire {
  schema: number;
  favourites: string[];
  folders: Array<{ id: string; label: string; items: string[] }>;
  hidden: string[];
  version?: number;
}
export interface NavLayoutResponse {
  layout: NavLayoutWire | null;
  /** Present only while the user has no stored layout: the SPEC-452 pins. */
  legacy_favourites?: string[];
  locked: string[];
  version: number;
}
export const fetchNavLayout = () => request<NavLayoutResponse>('/api/user/nav-layout');
export const saveNavLayout = (baseVersion: number, layout: Omit<NavLayoutWire, 'version'>) =>
  request<{ layout: NavLayoutWire; version: number }>('/api/user/nav-layout', {
    method: 'PUT',
    body: JSON.stringify({ base_version: baseVersion, layout }),
  });

// SPEC-563 — named personal menu presets (folders + hidden; never the favourites).
export interface NavPreset {
  id: string;
  name: string;
  preset: { folders: NavLayoutWire['folders']; hidden: string[] };
  created_at: string | null;
  updated_at: string | null;
}
export const fetchNavPresets = () => request<{ presets: NavPreset[] }>('/api/user/nav-presets');
export const createNavPreset = (name: string, preset: NavPreset['preset']) =>
  request<NavPreset>('/api/user/nav-presets', { method: 'POST', body: JSON.stringify({ name, preset }) });
export const renameNavPreset = (id: string, name: string) =>
  request<NavPreset>(`/api/user/nav-presets/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
export const deleteNavPreset = (id: string) =>
  request<{ ok: boolean }>(`/api/user/nav-presets/${encodeURIComponent(id)}`, { method: 'DELETE' });

// SPEC-563 — admin read-only view of every login's menu (admin / super_admin only).
export interface AdminNavLayoutRow {
  username: string;
  full_name: string;
  role: string;
  customised: boolean;
  folders: number;
  hidden: number;
  updated_at: string | null;
  layout: NavLayoutWire | null;
  rendered: Array<{ id: string; label: string; items: string[] }> | null;
}
export const fetchAdminNavLayouts = () => request<{ users: AdminNavLayoutRow[] }>('/api/admin/nav-layouts');

// SPEC-146 — Rulings Desk (the Gap Register as in-app Q&A). Answers are recorded
// rulings awaiting human application — this endpoint never changes any behaviour.
export interface RulingOption { key: string; label: string; detail?: string; recommended?: boolean }
// SPEC-224 — a peer's existing answer on a multi-person card, computed server-side at
// serve time. Present only when the audience names more than one person and one of the
// others has already answered.
export interface RulingPeerAnswer {
  author: string;
  option_key: string | null;
  option_label: string | null;
  answer_text: string | null;
  concurrence: boolean;
  answered_at: string | null;
}
// SPEC-327 — owner attribution. `expected` + `why` render for every viewer of the card
// (the card is already audience-scoped); the ranked `ranking` fallback list is served to
// admin/super_admin viewers only.
export interface RulingOwnerRank { user: string; why: string }
export interface RulingOwner { expected: string; why: string; ranking?: RulingOwnerRank[] }
// SPEC-327 — a provisional decision Param recorded because the owner did not answer in time.
// Present only once recorded; the owner revises it through the normal answer flow.
export interface RulingProvisional {
  decided_by: string;
  decided_at: string | null;
  decision: string;
  rationale: string | null;
  revisable: boolean;
}
// SPEC-584 — "Not my decision". `reroute_suggestion` is who we think owns the card (shown
// with the action); `rerouted_from` is the chip on the new owner's desk.
export interface RulingRerouteSuggestion { username: string; display_name: string; why: string }
export interface RulingReroutedFrom {
  username: string;
  display_name: string;
  note: string | null;
  at: string | null;
}
export interface RulingPerson { username: string; display_name: string; role: string | null }
export interface RulingCard {
  gap_id: string;
  version: number;
  title: string;
  what_this_is: string;
  why_asking: string;
  running_today: string;
  options: RulingOption[];
  allow_free_text: boolean;
  peer_answer?: RulingPeerAnswer;
  // SPEC-327 additions (all optional; older payloads omit them).
  owner?: RulingOwner;
  answer_by?: string | null;
  is_overdue?: boolean;
  provisional?: RulingProvisional;
  status?: string;
  // SPEC-584 additions (optional; older payloads omit them).
  reroute_suggestion?: RulingRerouteSuggestion;
  rerouted_from?: RulingReroutedFrom;
}
export interface RulingMineItem {
  gap_id: string;
  title: string;
  option_key: string | null;
  option_label: string | null;
  answer_text: string | null;
  discussed: boolean;
  created_at: string | null;
  chip: 'Recorded' | 'Applied';
}
export const fetchRulingsPending = () =>
  request<{ cards: RulingCard[]; count: number }>('/api/rulings/pending');
export const fetchRulingsMine = () =>
  request<{ answers: RulingMineItem[]; count: number }>('/api/rulings/mine');
export const submitRulingAnswer = (
  gapId: string,
  body: { option_key?: string | null; answer_text?: string | null; discussed: boolean; concurrence?: boolean },
) =>
  request<any>(`/api/rulings/${encodeURIComponent(gapId)}/answer`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// SPEC-584 — pass a card to the person who owns it. Recorded only; the registers are
// updated by Param, never by this call.
export const fetchRulingsPeople = () =>
  request<{ people: RulingPerson[]; count: number }>('/api/rulings/people');
export const rerouteRuling = (
  gapId: string,
  body: { to_username: string; note?: string | null; keep_me?: boolean },
) =>
  request<any>(`/api/rulings/${encodeURIComponent(gapId)}/reroute`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// SPEC-327 — admin/super_admin records a provisional decision on an overdue card. Records an
// audit row + notifies the owner; changes no behaviour and auto-applies nothing.
export const recordProvisionalDecision = (
  gapId: string,
  body: { decision: string; rationale?: string | null },
) =>
  request<any>(`/api/rulings/${encodeURIComponent(gapId)}/provisional`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Forecast vs Actuals explorer (SPEC-150 - G5 verification tool). Read-only over the
// as-filed trail; accuracy uses the shared WAPE/bias definition.
export const fetchForecastHistory = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/forecast/history/series' + qs);
};

// SPEC-243 / G42 — fill rate (ordered vs invoiced units), the service measurement. Readiness-
// gated: weeks the invoice feed doesn't yet span come back not_yet_measurable (never 0%).
export const fetchFillRate = (params?: Record<string, string>) => {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<any>('/api/measure/fill-rate' + qs);
};

// SPEC-403 — pre-MPS production upload (production's weekly build plan). The upload is
// validated row by row: accepted rows are stored (versioned); rejected rows ride the
// report with a per-row reason so nothing fails silently.
export interface PreMpsRejectedRow { row: number | null; fg_sku: string | null; reasons: string[]; }
export interface PreMpsRow {
  source_row: number | null; factory: string | null; production_line: string | null;
  fg_sku: string | null; fg_description: string | null; production_date: string | null;
  production_week: string | null; quantity_cases: number | null; quantity_pallets: number | null;
  quantity_kg: number | null; production_reason: string | null; campaign: string | null;
  priority: string | null; comments: string | null; flags: string | null;
}
export interface PreMpsHeader {
  planning_version: string | null; upload_version: string | null; planner: string | null;
  header_factory: string | null; business_unit: string | null; upload_date: string | null;
  source_file?: string | null; uploaded_by?: string | null; uploaded_at?: string | null;
}
export interface PreMpsUploadResult {
  upload_version: string; header: PreMpsHeader; message: string;
  counts: { accepted: number; rejected: number; skipped: number; flagged: number; stored?: number; replaced?: number };
  accepted: PreMpsRow[]; rejected: PreMpsRejectedRow[]; skipped_rows: number[];
}
export interface PreMpsLatest {
  version: string | null; found: boolean; header: PreMpsHeader | null;
  rows: PreMpsRow[]; counts: { rows: number };
}
export const uploadPreMps = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return request<PreMpsUploadResult>('/api/demand/pre-mps-upload', { method: 'POST', body: fd });
};
export const fetchPreMpsLatest = (version?: string) => {
  const q = version ? `?version=${encodeURIComponent(version)}` : '';
  return request<PreMpsLatest>(`/api/demand/pre-mps-upload/latest${q}`);
};
// SPEC-416 — downloadable sample/template workbook (Sudhin ask 18 Sep): same auth-gated
// GET pattern as downloadReportCSV, but the body is an .xlsx blob, not JSON.
export async function downloadPreMpsSample(): Promise<void> {
  const res = await requestRaw('/api/demand/pre-mps-upload/sample');
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : 'pre_mps_production_upload_sample.xlsx';
  triggerBlobDownload(blob, filename);
}

// SPEC-532 — the WHOLE published Box A book (customer × line × week) as one CSV, pinned to
// the version the screen shows. Same auth-gated GET pattern as downloadPreMpsSample.
export async function downloadBoxABookCsv(planningVersion: string | null): Promise<void> {
  const q = planningVersion ? `?planning_version=${encodeURIComponent(planningVersion)}` : '';
  const res = await requestRaw(`/api/box-a/export/csv${q}`);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  triggerBlobDownload(blob, match ? match[1] : 'box_a_book.csv');
}

// SPEC-480: report packs (the weekly leadership PDF). All four calls are behind the
// report_export capability on the server; the page hides the card without it.
export interface ReportPackRun {
  run_id: string;
  status: 'building' | 'ready' | 'failed' | 'awaiting_data';
  pack_type?: string;
  trigger?: string;
  requested_by?: string | null;
  failure_reason?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  data_as_of?: string | null;
  planning_safe?: boolean | null;
  artefact_bytes?: number | null;
}
export interface ReportPackRunList {
  pack: string;
  in_flight: { run_id: string; started_at: string; requested_by?: string | null } | null;
  runs: ReportPackRun[];
}
export const fetchReportPackRuns = (pack: string) =>
  request<ReportPackRunList>(`/api/reports/packs/${encodeURIComponent(pack)}/runs`);
export const startReportPackRun = (pack: string) =>
  request<{ run_id: string; status: 'building'; started: boolean }>(
    `/api/reports/packs/${encodeURIComponent(pack)}/runs`, { method: 'POST' });
export const fetchReportPackRun = (runId: string) =>
  request<ReportPackRun>(`/api/reports/packs/runs/${encodeURIComponent(runId)}`);
export async function downloadReportPack(runId: string): Promise<void> {
  const res = await requestRaw(`/api/reports/packs/runs/${encodeURIComponent(runId)}/download`);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  triggerBlobDownload(blob, match ? match[1] : 'weekly-leadership-report.pdf');
}
// SPEC-555: the weekly leadership reports kept week by week (same report_export gate).
export interface ReportHistoryRun {
  run_id: string;
  week: string;
  kind: 'weekly' | 'ad_hoc';
  built_by: string;
  status: 'ready' | 'failed' | 'awaiting_data';
  failure_reason?: string | null;
  finished_at?: string | null;
  data_as_of?: string | null;
  planning_safe?: boolean | null;
  sales_week_start?: string | null;
  sales_week_end?: string | null;
  figures_kept: boolean;
  figures_captured?: 'at_run' | 'backfill_from_stored_figures' | null;
  figures_captured_at?: string | null;
  figures_captured_text?: string | null;
  promoted_by?: string | null;
  promoted_at?: string | null;
  promoted_text?: string | null;
  downloadable: boolean;
}
export interface ReportWeek {
  week: string;
  label: string;
  week_start: string;
  status: 'record' | 'failed' | 'awaiting_data' | 'building' | 'due' | 'none_yet' | 'before_first' | 'missed';
  no_record_text: string | null;
  record: ReportHistoryRun | null;
  other_runs: ReportHistoryRun[];
}
export interface ReportWeeksResponse {
  pack: string;
  current_week: string | null;
  first_weekly_week: string | null;
  kept_since: string | null;
  weeks: ReportWeek[];
}
export interface ReportTrendPoint {
  week: string;
  label: string;
  week_start: string;
  value: number | null;
  state: string | null;
  gap_reason: string | null;
  note: string | null;
  metric_version: string | null;
  definition_changed: boolean;
  run_id: string | null;
  as_of: string | null;
  captured?: string | null;
  captured_at?: string | null;
  captured_text?: string | null;
}
export interface ReportTrendMove {
  metric_key: string;
  label: string;
  sentence: string;
  direction: 'up' | 'down' | 'same' | null;
  basis: string;
}
export interface ReportTrendSeries {
  metric_key: string;
  label: string;
  basis: string;
  unit: 'gbp' | 'count';
  points: ReportTrendPoint[];
  moved: ReportTrendMove;
}
export interface ReportTrendsResponse {
  pack: string;
  weeks: number;
  kept_since: string | null;
  axis: { week: string; label: string; week_start: string; has_record: boolean; no_record_text: string | null }[];
  series: ReportTrendSeries[];
  moved: ReportTrendMove[];
}
export const fetchReportWeeks = (pack: string, limit = 52) =>
  request<ReportWeeksResponse>(`/api/reports/packs/${encodeURIComponent(pack)}/weeks?limit=${limit}`);
export const fetchReportTrends = (pack: string, weeks: number) =>
  request<ReportTrendsResponse>(`/api/reports/packs/${encodeURIComponent(pack)}/trends?weeks=${weeks}`);
async function downloadNamed(path: string, fallback: string): Promise<void> {
  const res = await requestRaw(path);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  triggerBlobDownload(blob, match ? match[1] : fallback);
}
export const makeReportRunTheRecord = (runId: string) =>
  request<{ run_id: string; week: string; promoted_by: string; promoted_at: string }>(
    `/api/reports/packs/runs/${encodeURIComponent(runId)}/make-record`, { method: 'POST' });
export const downloadAllReportWeeks = (pack: string, limit = 52) =>
  downloadNamed(`/api/reports/packs/${encodeURIComponent(pack)}/weeks/download?limit=${limit}`,
    'weekly-leadership-reports.zip');
export const downloadReportTrendsCsv = (pack: string, weeks: number) =>
  downloadNamed(`/api/reports/packs/${encodeURIComponent(pack)}/trends/csv?weeks=${weeks}`,
    'weekly-leadership-report-trends.csv');
// SPEC-548: every product x week of "Three forecasts" as one CSV (csv_export on the server).
// `query` is the same filter string the product table uses (lib/threeForecastsApi.filterQuery).
export async function downloadThreeForecastsCsv(query: string): Promise<void> {
  const res = await requestRaw(`/api/three-forecasts/csv${query}`);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') || '';
  const match = cd.match(/filename="?([^"]+)"?/);
  triggerBlobDownload(blob, match ? match[1] : 'three_forecasts.csv');
}
